'use client';

import { IconFrenchFlag, IconPortugueseFlag, IconSpanishFlag, IconUsaFlag } from '@/components/ui/icons';
import { usePathname, useRouter } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { ChevronDown } from 'lucide-react';
import type { Locale } from 'next-intl';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState, useTransition } from 'react';

type Props = {
    children: ReactNode;
    defaultValue: string;
    label: string;
    className?: string;
};

export default function LocaleSwitcherSelect({ defaultValue, label, className = '' }: Props) {
    const router = useRouter();
    const [isPending, startTransition] = useTransition();
    const pathname = usePathname();
    const params = useParams();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && event.target instanceof Node && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) document.addEventListener('click', handleClickOutside);

        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    const t = useTranslations('LocaleSwitcher');

    const currentLocal = routing.locales.find((cur) => defaultValue === cur);

    function onSelectChange(nextLocale: Locale) {
        startTransition(() => {
            router.replace(
                // @ts-expect-error -- TypeScript will validate that only known `params`
                // are used in combination with a given `pathname`. Since the two will
                // always match for the current route, we can skip runtime checks.
                { pathname, params },
                { locale: nextLocale },
            );
        });
    }

    const flags = [
        { local: 'fr', icon: <IconFrenchFlag className="mr-3" /> },
        { local: 'pt', icon: <IconPortugueseFlag className="mr-3" /> },
        { local: 'es', icon: <IconSpanishFlag className="mr-3" /> },
        { local: 'en', icon: <IconUsaFlag className="mr-3" /> },
    ];

    return (
        <div ref={containerRef} className="relative">
            <p className="sr-only">{label}</p>
            <button
                className={`${className} appearance-none bg-transparent py-3 pl-3 pr-8 cursor-pointer`}
                type="button"
                onClick={() => setIsOpen(!isOpen)}
            >
                {t('locale', { locale: currentLocal ? currentLocal : 'fr' })}
            </button>
            <ul
                className={`
                ${isOpen ? 'block' : 'hidden'}
                ${isPending ? 'disabled' : ''}
                min-w-[200px]
                px-5 absolute bg-[rgba(14,14,14,0.70)] rounded-[8px]
            `}
            >
                {routing.locales.map((cur) => (
                    <li
                        className={`
                        flex rounded-[8px] px-[10px] py-2 my-2 relative text-base
                        hover:bg-[rgba(255,255,255,0.30)] cursor-pointer
                        after:content-[''] after:absolute
                        after:-bottom-1 after:left-0
                        after:w-full after:h-[1px]
                        after:bg-[rgba(255,255,255,0.10)]
                        last:after:content-[none]`}
                        key={cur}
                        value={cur}
                        onClickCapture={() => {
                            onSelectChange(cur);
                            setIsOpen(false);
                        }}
                    >
                        {flags.find((f) => f.local === cur)?.icon} {t('locale', { locale: cur })}
                    </li>
                ))}
            </ul>
            <span className="pointer-events-none absolute right-1 text-lg top-3">
                <ChevronDown />
            </span>
        </div>
    );
}
